/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useState} from "react"
import moment from "moment"
import {useTranslation} from "react-i18next"
import {useMutation, useQuery} from "@tanstack/react-query"
import {BaseButton} from "components/buttons/Button/BaseButton"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import {KlassappTableProps} from "types/common"
import {formatDateOnly, handleError} from "helpers"
import {useModel} from "hooks"
import {sapCheckpointsService} from "services"
import {KlassappTableHOC} from "HOC"
import {sapCheckpointKindOptions, SapCheckpointKind} from "types/settings/sap-checkpoint"
import {SapCheckpointCheck} from "types/settings/sap-checkpoint-check"
import styles from "./SapChecks.module.css"

type Props = KlassappTableProps & {
    studentId: number
}

function formatHours(value: number | null | undefined) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return ""
    }
    return String(Number(value))
}

function formatCheckedAt(value: string | null | undefined, dateFormat: string) {
    if (!value) {
        return ""
    }
    return moment(value).format(`${dateFormat} hh:mm A`)
}

function displayValue(value: string | number | null | undefined) {
    if (value === null || value === undefined || value === "") {
        return ""
    }
    return String(value)
}

function SapChecks(props: Props) {
    const {
        page,
        total,
        pageSize,
        columns,
        data,
        allFields,
        fields,
        isShowTableHeaderAction,
        tableHeaderActions,
        orderField,
        dispatch,
        dispatchFunc,
        tableRef,
        onChangePage,
        onChangeRowPerPage,
        onChangeFields,
        onChangeAllFields,
        onDraggableColumn,
        onUpdateRowData,
        onUpdateTableData,
        onClickSortColumn,
        studentId
    } = props
    const {t} = useTranslation(["academics", "user"])
    const model = useModel()
    const dateFormat = model.getUserDateFormat()
    const [rerunningId, setRerunningId] = useState<number | null>(null)

    const getSapKindLabel = (sapKind: SapCheckpointCheck["sapKind"]) => {
        const option = sapCheckpointKindOptions.find((kind) => kind.id === sapKind)
        return option ? t(`academics.sapChecks.kind.${option.id}`) : sapKind
    }

    const getStatusLabel = (item: SapCheckpointCheck) => {
        const statusCode = item.jsonData?.status
        if (!statusCode) {
            return ""
        }
        const statusLabel = t(`academics.sapChecks.sapStatus.${statusCode}`, {defaultValue: statusCode})
        if (item.sapKind === SapCheckpointKind.FA) {
            return t("academics.sapChecks.titleIvStatus", {status: statusLabel})
        }
        return statusLabel
    }

    const decorateItem = (item: SapCheckpointCheck) => {
        const versionName = item.programVersion?.versionName
        const versionCode = item.programVersion?.versionCode
        const programVersion = versionCode ? `${versionName} (${versionCode})` : versionName
        return {
            ...item,
            id: item.sapCheckpointCheckId,
            firstName: item.student?.firstName || "",
            lastName: item.student?.lastName || "",
            recordTypeLabel: item.isRerun ? t("academics.sapChecks.rerunRecord") : t("academics.sapChecks.check"),
            sapKindLabel: getSapKindLabel(item.sapKind),
            incrementNumberLabel: displayValue(item.jsonData?.incrementNumber || null),
            dateRunLabel: formatDateOnly(item.jsonData?.dateRun, dateFormat),
            dateHitLabel: formatDateOnly(item.jsonData?.dateHit, dateFormat),
            statusLabel: getStatusLabel(item),
            attendancePercentageLabel: displayValue(item.jsonData?.attendancePercentage),
            gradeLabel: displayValue(item.jsonData?.grade),
            programName: item.program?.name || "",
            programVersionName: programVersion || "",
            shiftNames: (item.shifts ?? [])
                .map((shift) => shift.name)
                .filter(Boolean)
                .join(", "),
            checkpointHoursLabel: formatHours(item.checkpointHours),
            completedHoursLabel: formatHours(item.completedHours),
            checkedAtLabel: formatCheckedAt(item.checkedAt, dateFormat),
            rerunResultLabel: item.rerunResult ? t(`academics.sapChecks.result.${item.rerunResult}`) : "",
            customUserId: item.customUserId || "",
            enrollmentId: item.enrollmentId || "",
            startingTermName: item.startingTermName || "",
            studentStatusName: item.studentStatusName || "",
            campusNames: (item.campusNames ?? []).filter(Boolean).join(", "),
            currentCompletedHoursLabel: formatHours(item.currentCompletedHours),
            ldaLabel: formatDateOnly(item.lda, dateFormat)
        }
    }

    const checksQuery = useQuery(
        ["sap-checkpoint-checks", studentId, page, pageSize],
        async () => {
            const {data: checks, total: checksTotal} = await sapCheckpointsService.listChecks({
                filter: {studentProfileIds: [studentId]},
                range: {page, pageSize},
                sort: {orderBy: "checkedAt", orderDir: "DESC"},
                linkedEntities: true
            })
            return {data: checks.map(decorateItem), total: checksTotal}
        },
        {
            enabled: !!studentId,
            keepPreviousData: true,
            onSuccess: (result) => {
                dispatch({data: result.data, total: result.total})
            },
            onError: (error) => {
                handleError(error)
            }
        }
    )

    const rerunMutation = useMutation(
        (sapCheckpointCheckId: number) => sapCheckpointsService.rerunCheck(sapCheckpointCheckId),
        {
            onSuccess: () => {
                checksQuery.refetch()
            },
            onError: (error) => {
                handleError(error)
            },
            onSettled: () => {
                setRerunningId(null)
            }
        }
    )

    const onRerun = (sapCheckpointCheckId: number) => {
        if (rerunMutation.isLoading) {
            return
        }
        setRerunningId(sapCheckpointCheckId)
        rerunMutation.mutate(sapCheckpointCheckId)
    }

    const getFields = () => [
        t("user:firstName"),
        t("user:lastName"),
        t("user:userId"),
        t("academics.sapChecks.enrollmentId"),
        t("academics.sapChecks.startingTerm"),
        t("academics.sapChecks.filters.studentStatus"),
        t("academics.sapChecks.campus"),
        t("academics.sapChecks.currentCompletedHours"),
        t("academics.sapChecks.lda"),
        t("academics.sapChecks.recordType"),
        t("academics.sapChecks.sapKind"),
        t("academics.sapChecks.incrementNumber"),
        t("academics.sapChecks.dateRun"),
        t("academics.sapChecks.dateHit"),
        t("academics.sapChecks.status"),
        t("academics.sapChecks.attendancePercentage"),
        t("academics.sapChecks.grade"),
        t("academics.sapChecks.program"),
        t("academics.sapChecks.programVersion"),
        t("academics.sapChecks.shifts"),
        t("academics.sapChecks.checkpointHours"),
        t("academics.sapChecks.completedHours"),
        t("academics.sapChecks.checkedAt"),
        t("academics.sapChecks.resultTitle"),
        t("academics.sapChecks.rerun")
    ]

    const getColumns = () => [
        {title: t("user:firstName"), field: "firstName", style: {minWidth: "140px"}},
        {title: t("user:lastName"), field: "lastName", style: {minWidth: "140px"}},
        {title: t("user:userId"), field: "customUserId", style: {minWidth: "140px"}},
        {title: t("academics.sapChecks.enrollmentId"), field: "enrollmentId", style: {minWidth: "140px"}},
        {title: t("academics.sapChecks.startingTerm"), field: "startingTermName", style: {minWidth: "150px"}},
        {
            title: t("academics.sapChecks.filters.studentStatus"),
            field: "studentStatusName",
            style: {minWidth: "150px"}
        },
        {title: t("academics.sapChecks.campus"), field: "campusNames", style: {minWidth: "160px"}},
        {
            title: t("academics.sapChecks.currentCompletedHours"),
            field: "currentCompletedHoursLabel",
            style: {minWidth: "180px"}
        },
        {title: t("academics.sapChecks.lda"), field: "ldaLabel", style: {minWidth: "130px"}},
        {title: t("academics.sapChecks.recordType"), field: "recordTypeLabel", style: {minWidth: "120px"}},
        {title: t("academics.sapChecks.sapKind"), field: "sapKindLabel", style: {minWidth: "140px"}},
        {title: t("academics.sapChecks.incrementNumber"), field: "incrementNumberLabel", style: {minWidth: "110px"}},
        {title: t("academics.sapChecks.dateRun"), field: "dateRunLabel", style: {minWidth: "120px"}},
        {title: t("academics.sapChecks.dateHit"), field: "dateHitLabel", style: {minWidth: "120px"}},
        {title: t("academics.sapChecks.status"), field: "statusLabel", style: {minWidth: "160px"}},
        {
            title: t("academics.sapChecks.attendancePercentage"),
            field: "attendancePercentageLabel",
            style: {minWidth: "130px"}
        },
        {title: t("academics.sapChecks.grade"), field: "gradeLabel", style: {minWidth: "100px"}},
        {title: t("academics.sapChecks.program"), field: "programName", style: {minWidth: "160px"}},
        {title: t("academics.sapChecks.programVersion"), field: "programVersionName", style: {minWidth: "180px"}},
        {title: t("academics.sapChecks.shifts"), field: "shiftNames", style: {minWidth: "140px"}},
        {title: t("academics.sapChecks.checkpointHours"), field: "checkpointHoursLabel", style: {minWidth: "140px"}},
        {title: t("academics.sapChecks.completedHours"), field: "completedHoursLabel", style: {minWidth: "140px"}},
        {title: t("academics.sapChecks.checkedAt"), field: "checkedAtLabel", style: {minWidth: "170px"}},
        {title: t("academics.sapChecks.resultTitle"), field: "rerunResultLabel", style: {minWidth: "160px"}},
        {title: t("academics.sapChecks.rerun"), field: "rerunHtml", style: {minWidth: "120px"}}
    ]

    useEffect(() => {
        dispatch({isClassComponent: false, page: 1})
    }, [studentId])

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: () => t("academics.sapChecks.tab")},
            {key: "getListData", func: checksQuery.refetch},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
    }, [])

    const rows = (data ?? []).map((item) => ({
        ...item,
        rerunHtml:
            item.rerunCount > 0 ? (
                <span>{t("academics.sapChecks.alreadyRerun")}</span>
            ) : (
                <div className={styles.action}>
                    <BaseButton
                        title={t("academics.sapChecks.rerun")}
                        variant="secondary"
                        uppercase={false}
                        className={styles.rerunButton}
                        loading={rerunningId === item.sapCheckpointCheckId}
                        disabled={rerunMutation.isLoading}
                        onClick={(event) => {
                            event.stopPropagation()
                            onRerun(item.sapCheckpointCheckId)
                        }}
                    />
                </div>
            )
    }))

    return (
        <div>
            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={onChangePage}
                onChangeRowPerPage={onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={onChangeFields}
                onChangeAllFields={onChangeAllFields}
                onDraggableColumn={onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                data={rows}
                menuActions={[]}
                isLoading={checksQuery.isFetching}
                fields={fields}
                allFields={allFields}
                orderField={orderField}
                tableRef={tableRef}
                emptyText={t("academics.sapChecks.empty")}
                onClickRowItem={() => {}}
                onChangeFields={onChangeFields}
                onUpdateRowData={onUpdateRowData}
                onUpdateTableData={onUpdateTableData}
                onClickSortColumn={onClickSortColumn}
                onDraggableColumn={onDraggableColumn}
                onChangeAllFields={onChangeAllFields}
            />
        </div>
    )
}

export default KlassappTableHOC(SapChecks)
